import { forwardRef, useImperativeHandle, useLayoutEffect, useMemo, useRef } from "react";
import { cx } from "@/utils/cx";

const TOKEN_PATTERN = /(\{[a-z_]+(?:\([a-z_]+\))?\})/gi;
const WHOLE_TOKEN = new RegExp(`^${TOKEN_PATTERN.source}$`, "i");
const MAX_AUTO_HEIGHT = 320;

type HighlightedTextareaProps = {
  value: string;
  flaggedTokens: string[];
  onChange: (value: string) => void;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  maxLength?: number;
  minHeight?: number;
  className?: string;
  textClassName?: string;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
};

export const HighlightedTextarea = forwardRef<HTMLTextAreaElement, HighlightedTextareaProps>(
  (
    {
      value,
      flaggedTokens,
      onChange,
      onBlur,
      onKeyDown,
      placeholder,
      autoFocus,
      disabled,
      maxLength,
      minHeight = 110,
      className,
      textClassName = "p-3.5 text-sm leading-6",
      ...aria
    },
    ref,
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const mirrorRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

    const flagged = useMemo(() => new Set(flaggedTokens), [flaggedTokens]);

    useLayoutEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_AUTO_HEIGHT)}px`;
      if (mirrorRef.current) mirrorRef.current.scrollTop = textarea.scrollTop;
    }, [value]);

    const metrics = "break-words whitespace-pre-wrap [scrollbar-gutter:stable]";

    return (
      <div className={cx("relative flex", className)}>
        <div
          ref={mirrorRef}
          aria-hidden
          className={cx("pointer-events-none absolute inset-0 overflow-hidden text-primary", metrics, textClassName)}
        >
          {value.split(TOKEN_PATTERN).map((part, index) =>
            WHOLE_TOKEN.test(part) ? (
              <span
                key={index}
                className={cx(
                  "rounded-[3px]",
                  flagged.has(part) ? "bg-warning-50 text-warning-700" : "bg-brand-50 text-brand-700",
                )}
              >
                {part}
              </span>
            ) : (
              part
            ),
          )}
          {value.endsWith("\n") ? " " : null}
        </div>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onScroll={(e) => {
            if (mirrorRef.current) mirrorRef.current.scrollTop = e.currentTarget.scrollTop;
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          maxLength={maxLength}
          rows={1}
          className={cx(
            "relative w-full resize-none overflow-y-auto bg-transparent text-transparent caret-gray-900 outline-hidden placeholder:text-placeholder disabled:cursor-not-allowed",
            metrics,
            textClassName,
          )}
          style={{ minHeight, maxHeight: MAX_AUTO_HEIGHT }}
          {...aria}
        />
      </div>
    );
  },
);

HighlightedTextarea.displayName = "HighlightedTextarea";
